import { useEffect } from 'react';

import { useSearchParams } from '@remix-run/react';

import { useNonRevalidatingFetcher } from '~/hooks/useNonRevalidatingFetcher';

import type { SetCookieOptions } from '../useUtils/useCookies';

export const VISIT_TOKEN_DURATION_MINUTES = 30;
export const VISIT_TOKEN_READY_EVENT_NAME = 'visitTokenReady';

interface Props {
  shouldCreateVisitToken: boolean;
  setCookie: (options: SetCookieOptions) => void;
}

type FetcherData = {
  visitToken: string;
};

const useCreateVisitToken = ({ shouldCreateVisitToken, setCookie }: Props) => {
  const [searchParams] = useSearchParams();
  const fetcher = useNonRevalidatingFetcher<() => Promise<FetcherData>>();
  const data = fetcher.data;

  // Submit Fetch request to create a visit token
  useEffect(() => {
    if (shouldCreateVisitToken) {
      const referrer = document.referrer;
      const formData = new FormData();
      formData.append('referrer', referrer);
      formData.append('landing_page', window.location.href);

      const url = '/visit_token';
      const endpoint = url + (searchParams.toString() ? `?${searchParams.toString()}` : '');

      fetcher.submit(formData, { method: 'post', action: endpoint });
    }
  }, [shouldCreateVisitToken]);

  // Capture the visit token to store in cookies
  useEffect(() => {
    if (data?.visitToken && shouldCreateVisitToken) {
      setCookie({
        name: 'visit_token',
        value: data?.visitToken,
        minutes: VISIT_TOKEN_DURATION_MINUTES,
      });

      // Fire event when visit token is ready
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(VISIT_TOKEN_READY_EVENT_NAME));
      }
    }
  }, [data]);
};

export default useCreateVisitToken;
